import { useState } from 'react';
import { ChevronDown, CheckCircle2 } from 'lucide-react';
// @ts-expect-error JSX component without TS declarations
import CASDMExplorer from '@/components/CASDMExplorer';

const PRACTICES = [
  { id: 'appdev', label: 'App Development', available: true },
  { id: 'appmod', label: 'App Modification', available: false },
  { id: 'aiml', label: 'AI / ML', available: false },
  { id: 'migration', label: 'Migration', available: false },
] as const;

type PracticeId = (typeof PRACTICES)[number]['id'];

export default function CasdmPage(): JSX.Element {
  const [selected, setSelected] = useState<PracticeId>('appdev');
  const [open, setOpen] = useState(false);

  const currentPractice = PRACTICES.find((p) => p.id === selected)!;

  return (
    <div className="relative w-full">
      {/* Practice selector — positioned top-right within the page flow */}
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-text-primary">CASDM Explorer</h1>
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-2 rounded-[var(--radius-button)] border border-outlines bg-white px-4 py-2 text-sm font-medium text-text-primary shadow-sm transition-colors hover:bg-subtle-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <span
              className="h-2 w-2 rounded-full"
              style={{ background: currentPractice.available ? '#108d3e' : '#98a2b3' }}
            />
            {currentPractice.label}
            <ChevronDown
              className={`h-4 w-4 text-text-secondary transition-transform ${
                open ? 'rotate-180' : ''
              }`}
              aria-hidden="true"
            />
          </button>

          {open && (
            <div className="absolute right-0 mt-2 w-52 rounded-[var(--radius-card)] border border-outlines bg-white py-1 shadow-lg">
              {PRACTICES.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  disabled={!p.available}
                  onClick={() => {
                    if (p.available) {
                      setSelected(p.id);
                      setOpen(false);
                    }
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-text-primary transition-colors disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:bg-subtle-bg"
                >
                  {p.id === selected ? (
                    <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />
                  ) : (
                    <span className="h-4 w-4" />
                  )}
                  <span>{p.label}</span>
                  {!p.available && (
                    <span className="ml-auto rounded-full bg-outlines px-2 py-0.5 text-[10px] font-medium text-text-secondary">
                      Soon
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Explorer fills full width of the page content area */}
      <div className="w-full">
        <CASDMExplorer />
      </div>
    </div>
  );
}
